import CreateAgendaItemData from "@/domain/entities/agenda/CreateAgendaItemData";
import useCreateAgendaEvent from "@/hooks/agenda/useCreateAgendaEvent";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { cn } from "@/lib/utils";
import { createAgendaItemDataToAgendaItemMapper } from "@/mappers/agenda/agendaItemMapper";
import { CalendarCheck, Loader2, MapPin, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import EventEditForm from "../widgets/dashboard/calendar/eventModal/form/EventEditForm";
import {
    cardClassName,
    primaryButtonClassName,
    secondaryButtonClassName,
} from "./assistantClasses";
import { eventTimeRange } from "./EventDetailsBlock";
import eventProposalToCreateData from "./eventProposal";

type CreateAgendaItemResponseProps = {
    eventData: CreateAgendaItemData;
};

/**
 * The event the assistant proposes, as a card like the rest of the chat: the day, the title, when,
 * where, who is invited and the agenda. « Créer l'événement » creates it as proposed; « Modifier » opens
 * the calendar's form in its light look (the same form as the dashboard's, on a white card) to change it first.
 */
const CreateAgendaItemResponse = ({
    eventData,
}: CreateAgendaItemResponseProps) => {
    const { t, i18n } = useTranslation();
    const { data: user } = useGetUserProfile();
    const [state, setState] = useState<"proposed" | "editing" | "created">(
        "proposed",
    );
    const event = useMemo(
        () => createAgendaItemDataToAgendaItemMapper(eventData, user),
        [eventData, user],
    );
    const { createAgendaEvent } = useCreateAgendaEvent(() =>
        setState("created"),
    );
    const attendees = eventData.attendees ?? [];
    const day = (options: Intl.DateTimeFormatOptions) =>
        event.start.toLocaleDateString(i18n.language, options);
    const when = [
        day({ weekday: "long", day: "numeric", month: "long" }),
        eventTimeRange(
            event,
            i18n.language,
            t("dashboard.widgets.calendar.event.allDay"),
        ),
    ].join(" · ");

    if (state === "created") {
        return (
            <p className="flex items-center gap-2 text-sm text-primary-darkest">
                <CalendarCheck
                    aria-hidden
                    className="size-4 flex-none text-primary-dark"
                />
                {t("vistassistant.createAgendaItemResponse.success")}
            </p>
        );
    }

    if (state === "editing" && user) {
        return (
            <section
                aria-label={t("vistassistant.createAgendaItemResponse.label")}
                className={cn(cardClassName, "w-full p-4")}
            >
                <EventEditForm
                    event={event}
                    onCancel={() => setState("proposed")}
                    onSuccess={() => setState("created")}
                    submitLabel={t(
                        "vistassistant.createAgendaItemResponse.create",
                    )}
                    tone="light"
                />
            </section>
        );
    }

    return (
        <section
            aria-label={t("vistassistant.createAgendaItemResponse.label")}
            className={cn(
                cardClassName,
                "w-full max-w-[720px] overflow-hidden",
            )}
        >
            <div className="flex items-start gap-3 px-4 py-3">
                <span className="flex w-11 flex-none flex-col items-center rounded-lg bg-assistant-lavender py-1 leading-tight">
                    <span className="text-[10px] font-semibold uppercase text-assistant-muted">
                        {day({ weekday: "short" })}
                    </span>
                    <span className="text-sm font-semibold text-primary-darkest">
                        {day({ day: "numeric" })}
                    </span>
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <p className="text-base font-semibold leading-snug text-primary-darkest">
                        {eventData.subject}
                    </p>
                    <p className="text-[13px] text-assistant-muted first-letter:uppercase">
                        {when}
                    </p>
                    {eventData.location && (
                        <p className="flex min-w-0 items-center gap-1 text-[13px] text-assistant-muted">
                            <MapPin
                                aria-hidden
                                className="size-3.5 flex-none"
                            />
                            <span className="truncate">
                                {eventData.location}
                            </span>
                        </p>
                    )}
                </div>
            </div>
            {attendees.length > 0 && (
                <div className="flex items-start gap-2 border-t border-assistant-cardBorder px-4 py-2.5">
                    <Users
                        aria-label={t(
                            "vistassistant.createAgendaItemResponse.attendees",
                        )}
                        className="mt-0.5 size-4 flex-none text-assistant-muted"
                        role="img"
                    />
                    <ul className="flex min-w-0 flex-wrap gap-1.5">
                        {attendees.map((attendee) => (
                            <li
                                key={attendee}
                                className="max-w-full truncate rounded-full bg-assistant-lavender px-2 py-px text-[13px] text-primary-darker"
                            >
                                {attendee}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {eventData.description && (
                <p className="whitespace-pre-wrap break-words border-t border-assistant-cardBorder px-4 py-3 text-sm leading-relaxed text-primary-darkest">
                    {eventData.description}
                </p>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-assistant-cardBorder px-4 py-2.5">
                <button
                    className={secondaryButtonClassName}
                    disabled={!user || createAgendaEvent.isPending}
                    onClick={() => setState("editing")}
                    type="button"
                >
                    {t("vistassistant.createAgendaItemResponse.edit")}
                </button>
                <button
                    className={cn(
                        primaryButtonClassName,
                        "inline-flex items-center gap-1.5",
                    )}
                    disabled={createAgendaEvent.isPending}
                    onClick={() =>
                        createAgendaEvent.mutate({
                            createAgendaItemData:
                                eventProposalToCreateData(event),
                        })
                    }
                    type="button"
                >
                    {createAgendaEvent.isPending && (
                        <Loader2
                            aria-hidden
                            className="size-3.5 animate-spin"
                        />
                    )}
                    {t("vistassistant.createAgendaItemResponse.create")}
                </button>
            </div>
        </section>
    );
};

export default CreateAgendaItemResponse;
